import { useState, useRef, useCallback, useEffect } from 'react';
import { filterAssistantLinks } from '@/utils/assistantLinks';
import { supportAssistantService } from '@/services/support-assistant.service';
import type {
  AssistantRef,
  AssistantContextTarget,
  AssistantSendOptions,
  AssistantStep,
  AssistantToolActivityDto,
  AssistantUiMessageDto,
  PendingActionCardDto,
  SseFrame,
} from '@/types/support-assistant';

/**
 * Splits an SSE buffer on blank lines. Returns complete frames plus the
 * incomplete remainder. Comment lines (": ping") are ignored.
 */
export function parseSseChunk(buffer: string): {
  frames: SseFrame[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const rest = parts.pop() ?? '';
  const frames: SseFrame[] = [];
  for (const part of parts) {
    let event = 'message';
    const dataLines: string[] = [];
    let hasField = false;
    for (const line of part.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
        hasField = true;
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
        hasField = true;
      }
    }
    if (hasField) frames.push({ event, data: dataLines.join('\n') });
  }
  return { frames, rest };
}

const getToken = () =>
  sessionStorage.getItem('token') ||
  sessionStorage.getItem('adminToken') ||
  localStorage.getItem('token') ||
  localStorage.getItem('adminToken') ||
  '';

const isAssistantRef = (r: any): r is AssistantRef =>
  !!r &&
  Number.isInteger(r.n) &&
  r.n > 0 &&
  typeof r.targetId === 'string' &&
  r.targetId.length <= 80;

export interface AssistantFailure {
  kind: 'http' | 'stream';
  text?: string;
  scope?: AssistantContextTarget | null;
  options?: AssistantSendOptions;
}

let localIdCounter = 0;
const localId = (prefix: string) => `${prefix}-${Date.now()}-${localIdCounter++}`;

const newMessage = (
  role: AssistantUiMessageDto['role'],
  text = '',
): AssistantUiMessageDto => ({
  id: localId(role),
  role,
  text,
  toolActivity: [],
  createdAt: new Date().toISOString(),
});

export function useAssistantChat(sessionId: string | null) {
  const [messages, setMessages] = useState<AssistantUiMessageDto[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvingCardIds, setResolvingCardIds] = useState<string[]>([]);
  const [lastFailure, setLastFailure] = useState<AssistantFailure | null>(null);
  const [closedTicket, setClosedTicket] = useState<{ ticketNumber: string } | null>(
    null,
  );
  const [openTicket, setOpenTicket] = useState<{
    id: string;
    ticketNumber: string;
    assigneeName?: string;
  } | null>(null);
  const [isSendingHq, setIsSendingHq] = useState(false);
  const openTicketRef = useRef(openTicket);
  openTicketRef.current = openTicket;
  const lastSendRef = useRef<{
    text: string;
    scope?: AssistantContextTarget | null;
    options?: AssistantSendOptions;
  } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  const inFlightCardsRef = useRef<Set<string>>(new Set());
  const sessionIdRef = useRef<string | null>(sessionId);
  sessionIdRef.current = sessionId;
  // Guards loadSession against out-of-order responses and against overwriting
  // a turn the user started while the fetch was in flight.
  const loadTokenRef = useRef(0);
  const turnEpochRef = useRef(0);
  const prevSessionIdRef = useRef<string | null>(sessionId);

  // Switching sessions: stop the old stream and drop the old thread so it is
  // never shown under the new session id.
  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) return;
    prevSessionIdRef.current = sessionId;
    loadTokenRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    readerRef.current = null;
    currentAssistantIdRef.current = null;
    setIsStreaming(false);
    setMessages([]);
    setError(null);
    setClosedTicket(null);
    setOpenTicket(null);
    setIsSendingHq(false);
  }, [sessionId]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const updateCurrent = useCallback(
    (fn: (m: AssistantUiMessageDto) => AssistantUiMessageDto) => {
      const id = currentAssistantIdRef.current;
      if (!id) return;
      setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)));
    },
    [],
  );

  const applyFrame = useCallback(
    (frame: SseFrame) => {
      let data: any;
      try {
        data = frame.data ? JSON.parse(frame.data) : {};
      } catch {
        return;
      }
      switch (frame.event) {
        case 'delta':
          if (typeof data.text === 'string') {
            updateCurrent((m) => ({ ...m, text: m.text + data.text }));
          }
          break;
        case 'tool': {
          const tool = data as AssistantToolActivityDto;
          updateCurrent((m) => {
            const exists = m.toolActivity.some(
              (t) => t.toolUseId === tool.toolUseId,
            );
            return {
              ...m,
              toolActivity: exists
                ? m.toolActivity.map((t) =>
                    t.toolUseId === tool.toolUseId ? { ...t, ...tool } : t,
                  )
                : [...m.toolActivity, tool],
            };
          });
          break;
        }
        case 'step': {
          if (typeof data.id !== 'string') break;
          const step = data as AssistantStep;
          updateCurrent((m) => {
            const steps = m.steps ?? [];
            const exists = steps.some((s) => s.id === step.id);
            return {
              ...m,
              steps: exists
                ? steps.map((s) => (s.id === step.id ? { ...s, ...step } : s))
                : [...steps, step],
            };
          });
          break;
        }
        case 'refs': {
          const refs = Array.isArray(data.refs)
            ? data.refs.filter(isAssistantRef)
            : [];
          updateCurrent((m) => ({ ...m, refs }));
          break;
        }
        case 'links': {
          const links = filterAssistantLinks(data.links);
          updateCurrent((m) => ({ ...m, links }));
          break;
        }
        case 'scope': {
          const sys = newMessage('system');
          const sc = data.scope;
          sys.scopeChange = sc
            ? { type: sc.type, label: sc.label }
            : { type: null };
          setMessages((prev) => {
            let idx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === 'user') {
                idx = i;
                break;
              }
            }
            if (idx < 0) return [...prev, sys];
            return [...prev.slice(0, idx), sys, ...prev.slice(idx)];
          });
          break;
        }
        case 'confirmation':
          updateCurrent((m) => ({ ...m, card: data.card }));
          break;
        case 'clarify':
          updateCurrent((m) => ({
            ...m,
            clarify: {
              question: data.question,
              options: data.options ?? [],
              ...(data.searchOptions?.length
                ? { searchOptions: data.searchOptions }
                : {}),
            },
          }));
          break;
        case 'notice': {
          const sys = newMessage('system');
          sys.notice = data;
          setMessages((prev) => [...prev, sys]);
          break;
        }
        case 'error':
          setError(data.message ?? 'stream_error');
          setLastFailure((prev) => ({ ...(prev ?? {}), kind: 'stream' }));
          break;
        case 'done': {
          const serverId = data.messageId;
          if (typeof serverId === 'string' && /^[a-f0-9]{24}$/.test(serverId)) {
            const localIdNow = currentAssistantIdRef.current;
            if (localIdNow) {
              setMessages((prev) =>
                prev.some((m) => m.id === serverId)
                  ? prev
                  : prev.map((m) => (m.id === localIdNow ? { ...m, id: serverId } : m)),
              );
              currentAssistantIdRef.current = serverId;
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [updateCurrent],
  );

  const streamFrames = useCallback(
    async (path: string, body?: unknown, userMessageId?: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      // Captured so a late 409 can never lock a session the user switched away from.
      const startSid = sessionIdRef.current;
      const startEpoch = turnEpochRef.current;
      const assistantId = currentAssistantIdRef.current;
      setIsStreaming(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      let httpOk = true;
      let ticketOpenPayload: { code?: string; ticketNumber?: string } | null = null;
      try {
        const response = await fetch(`${baseUrl}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${getToken()}`,
          },
          body: body === undefined ? undefined : JSON.stringify(body),
          signal: controller.signal,
        });
        if (response.status === 409) {
          let payload: { code?: string; ticketNumber?: string } | null = null;
          try {
            payload = await response.json();
          } catch {
            payload = null;
          }
          if (payload?.code === 'ticket_closed') {
            const stale =
              controller.signal.aborted ||
              sessionIdRef.current !== startSid ||
              turnEpochRef.current !== startEpoch;
            if (!stale) {
              setClosedTicket({ ticketNumber: payload.ticketNumber ?? '' });
              setMessages((prev) =>
                prev.filter((m) => m.id !== assistantId && m.id !== userMessageId),
              );
              currentAssistantIdRef.current = null;
            }
            return true;
          }
          if (payload?.code === 'ticket_open') ticketOpenPayload = payload;
        }
        if (response.status === 409 && ticketOpenPayload) {
          const stale =
            controller.signal.aborted ||
            sessionIdRef.current !== startSid ||
            turnEpochRef.current !== startEpoch;
          if (stale) return true;
          setOpenTicket((prev) => ({
            id: prev?.id ?? '',
            ticketNumber: ticketOpenPayload!.ticketNumber ?? prev?.ticketNumber ?? '',
            assigneeName: prev?.assigneeName,
          }));
          setMessages((prev) =>
            prev.filter((m) => m.id !== assistantId && m.id !== userMessageId),
          );
          currentAssistantIdRef.current = null;
          return 'ticket_open' as const;
        }
        if (!response.ok || !response.body) {
          httpOk = false;
          throw new Error(`HTTP ${response.status}`);
        }
        const reader = response.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseChunk(buffer);
          buffer = parsed.rest;
          parsed.frames.forEach(applyFrame);
        }
        const tail = parseSseChunk(`${buffer}\n\n`);
        tail.frames.forEach(applyFrame);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'stream_error');
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          readerRef.current = null;
          setIsStreaming(false);
        }
      }
      return httpOk as boolean | 'ticket_open';
    },
    [applyFrame],
  );

  const startAssistantTurn = useCallback(() => {
    const assistant = newMessage('assistant');
    currentAssistantIdRef.current = assistant.id;
    return assistant;
  }, []);

  const sendHqMessage = useCallback(async (text: string) => {
    const sid = sessionIdRef.current;
    const trimmed = text.trim();
    if (!sid || !trimmed) return;
    const optimistic = newMessage('user', trimmed);
    optimistic.requesterReply = {
      byName: '',
      at: optimistic.createdAt,
      event: 'message',
    };
    // Bumping the epoch keeps an in-flight loadSession from clobbering the optimistic bubble.
    turnEpochRef.current += 1;
    setMessages((prev) => [...prev, optimistic]);
    setError(null);
    setIsSendingHq(true);
    const isCurrent = () => sessionIdRef.current === sid;
    try {
      const saved = await supportAssistantService.sendHqMessage(sid, trimmed);
      if (!isCurrent()) return;
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? saved : m)));
    } catch (err: any) {
      if (!isCurrent()) return;
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 409 && code === 'ticket_closed') {
        setClosedTicket({ ticketNumber: err.response.data.ticketNumber ?? '' });
        setOpenTicket(null);
      } else if (status === 409 && code === 'no_open_ticket') {
        setOpenTicket(null);
      } else {
        setError(
          err?.response?.data?.message ??
            (err instanceof Error ? err.message : 'hq_message_error'),
        );
      }
    } finally {
      if (isCurrent()) setIsSendingHq(false);
    }
  }, []);

  const closeTicket = useCallback(async () => {
    const sid = sessionIdRef.current;
    const ticket = openTicketRef.current;
    if (!sid || !ticket?.id) return;
    const isCurrent = () => sessionIdRef.current === sid;
    try {
      const res = await supportAssistantService.closeTicket(ticket.id);
      if (!isCurrent()) return;
      setClosedTicket({ ticketNumber: res.ticketNumber });
      setOpenTicket(null);
    } catch (err: any) {
      if (!isCurrent()) return;
      if (err?.response?.status === 409 && err.response.data?.code === 'ticket_closed') {
        setClosedTicket({
          ticketNumber: err.response.data.ticketNumber ?? ticket.ticketNumber,
        });
        setOpenTicket(null);
      } else {
        setError(
          err?.response?.data?.message ??
            (err instanceof Error ? err.message : 'close_ticket_error'),
        );
        return;
      }
    }
    turnEpochRef.current += 1;
    await loadSessionRef.current(sid);
  }, []);

  const sendMessage = useCallback(
    async (
      text: string,
      scope?: AssistantContextTarget | null,
      options?: AssistantSendOptions,
    ) => {
      const sid = sessionIdRef.current;
      const trimmed = text.trim();
      if (!sid || !trimmed) return;
      if (openTicketRef.current) {
        await sendHqMessage(trimmed);
        return;
      }
      const user = newMessage('user', trimmed);
      turnEpochRef.current += 1;
      const assistant = startAssistantTurn();
      setMessages((prev) => [...prev, user, assistant]);
      setLastFailure(null);
      lastSendRef.current = { text: trimmed, scope, options };
      const body: Record<string, unknown> = { text: trimmed };
      if (scope && scope.type === 'module') {
        body.scopeType = 'module';
        body.page = {
          moduleKey: scope.moduleKey,
          pagePath: scope.pagePath,
          routeUrl: scope.routeUrl,
        };
      } else if (scope) {
        body.scopeType = scope.type;
        body.scopeId = scope.id;
      } else if (scope === null) {
        body.scopeType = 'none';
      }
      if (options?.intent) body.intent = options.intent;
      const ok = await streamFrames(
        `/support-assistant/sessions/${sid}/messages`,
        body,
        user.id,
      );
      if (ok === 'ticket_open') {
        if (sessionIdRef.current !== sid) return;
        await loadSessionRef.current(sid);
        if (!options?.intent && sessionIdRef.current === sid) {
          await sendHqMessage(trimmed);
        }
        return;
      }
      if (!ok) {
        setLastFailure({ kind: 'http', text: trimmed, scope, options });
      }
    },
    [streamFrames, startAssistantTurn, sendHqMessage],
  );

  const resume = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    turnEpochRef.current += 1;
    const assistant = startAssistantTurn();
    setMessages((prev) => [...prev, assistant]);
    setLastFailure(null);
    const res = await streamFrames(`/support-assistant/sessions/${sid}/resume`);
    if (res === 'ticket_open' && sessionIdRef.current === sid) {
      await loadSessionRef.current(sid);
    }
  }, [streamFrames, startAssistantTurn]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    readerRef.current?.cancel().catch(() => undefined);
    abortRef.current = null;
    readerRef.current = null;
    updateCurrent((m) => ({ ...m, stopped: true }));
    setIsStreaming(false);
  }, [updateCurrent]);

  const loadSessionRef = useRef<(id: string) => Promise<void>>(async () => undefined);
  const resumeRef = useRef<() => Promise<void>>(async () => undefined);
  const sendRef = useRef<
    (
      t: string,
      s?: AssistantContextTarget | null,
      o?: AssistantSendOptions,
    ) => Promise<void>
  >(async () => undefined);
  sendRef.current = sendMessage;
  resumeRef.current = resume;

  const retry = useCallback(async () => {
    const failure = lastFailure;
    if (!failure) return;
    const sid = sessionIdRef.current;
    if (failure.kind === 'http') {
      if (!failure.text) return;
      // drop the optimistic user + assistant pair
      setMessages((prev) => {
        const idx = prev.length - 2;
        if (
          idx >= 0 &&
          prev[idx].role === 'user' &&
          prev[idx].text === failure.text &&
          prev[idx + 1].role === 'assistant'
        ) {
          return prev.slice(0, idx);
        }
        return prev;
      });
      await sendRef.current(failure.text, failure.scope, failure.options);
    } else {
      if (sid) await loadSessionRef.current(sid);
      await resumeRef.current();
    }
  }, [lastFailure]);

  const replaceCard = useCallback((card: PendingActionCardDto) => {
    setMessages((prev) =>
      prev.map((m) => (m.card?.id === card.id ? { ...m, card } : m)),
    );
  }, []);

  const resolveCard = useCallback(
    async (cardId: string, kind: 'approve' | 'reject') => {
      if (inFlightCardsRef.current.has(cardId)) return;
      inFlightCardsRef.current.add(cardId);
      setResolvingCardIds((prev) => [...prev, cardId]);
      let shouldResume = false;
      try {
        const card =
          kind === 'approve'
            ? await supportAssistantService.approve(cardId)
            : await supportAssistantService.reject(cardId);
        replaceCard(card);
        shouldResume = true;
      } catch (err: any) {
        if (err?.response?.status === 409 && err.response.data?.card) {
          replaceCard(err.response.data.card);
        } else {
          setError(
            err?.response?.data?.message ??
              (err instanceof Error ? err.message : 'resolve_error'),
          );
        }
      } finally {
        inFlightCardsRef.current.delete(cardId);
        setResolvingCardIds((prev) => prev.filter((id) => id !== cardId));
      }
      if (shouldResume) await resume();
    },
    [replaceCard, resume],
  );

  const approve = useCallback(
    (cardId: string) => resolveCard(cardId, 'approve'),
    [resolveCard],
  );
  const reject = useCallback(
    (cardId: string) => resolveCard(cardId, 'reject'),
    [resolveCard],
  );

  const loadSession = useCallback(async (id: string) => {
    const token = ++loadTokenRef.current;
    const epoch = turnEpochRef.current;
    try {
      const detail = await supportAssistantService.getSession(id);
      // Superseded by a newer load/session switch, or a turn started meanwhile.
      if (token !== loadTokenRef.current) return;
      if (epoch !== turnEpochRef.current || abortRef.current) return;
      currentAssistantIdRef.current = null;
      setError(null);
      setClosedTicket(
        detail.session.closedTicket
          ? { ticketNumber: detail.session.closedTicket.ticketNumber }
          : null,
      );
      setOpenTicket(
        detail.session.openTicket
          ? {
              id: detail.session.openTicket.id,
              ticketNumber: detail.session.openTicket.ticketNumber,
              assigneeName: detail.session.openTicket.assigneeName,
            }
          : null,
      );
      setMessages(detail.messages);
    } catch (err) {
      if (token !== loadTokenRef.current) return;
      setError(err instanceof Error ? err.message : 'load_error');
    }
  }, []);
  loadSessionRef.current = loadSession;

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    stop,
    approve,
    reject,
    resume,
    loadSession,
    resolvingCardIds,
    lastFailure,
    retry,
    closedTicket,
    openTicket,
    sendHqMessage,
    closeTicket,
    isSendingHq,
  };
}
